/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { DataRepository, TableRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutingKeys, ColumnModel, FormActionTypeEnum, TableModel, ToastDuration, ToastService } from '@shared';
import { combineLatest, from, iif, Observable, of } from 'rxjs';
import { catchError, first, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { QueryParamsProvider } from '../../../routing/services';
import { SamplesTableProvider } from '../../../business-layer/samples';
import { AsyncValidatorsOrchestratorService } from '../async-validators';
import { CrossTableParams } from '../async-validators/models/cross-table-params-type';
import {
	IAsyncValidationsBuildResult,
	IValidationConstraint,
	ValidationKeys,
	ValidationsBuilderModel
} from '../models';
import { ValidationMessagesProviderService } from './validation-messages-provider.service';
import {
	StandardTableProvider,
	SampleTypeProvider,
	TableTypeProvider,
	DataByTableTypeProvider
} from '../../../business-layer';
import {
	UniqueSampleNumberBuilder,
	UniquePrimaryKeyBuilder,
	UniqueStandardNumberBuilder,
	NotSupportedCharactersBuilder
} from '../builders';
import { NormalizeFormValuesForDatabaseService } from '../../services';

@Injectable()
export class CrossTableValidationsBuilderService {
	private queryParamsMap: Map<string, string> = new Map();

	private validationMessagesDictionary: Record<string, string>[];
	private validatorFnMap: Record<string, AsyncValidatorFn[]>;
	private sharedData$: Observable<{ tableMetadata: TableModel; isQcSample: boolean }>;

	private entityName = this.queryParamsProvider.parentTableName;
	private childTableName = this.queryParamsProvider.childTableName;
	private projectNumber = this.queryParamsProvider.projectNumber;
	private formActionType: FormActionTypeEnum = this.queryParamsProvider.formActionType();

	public constructor(
		private queryParamsProvider: QueryParamsProvider,
		private standardTableProvider: StandardTableProvider,
		private tableRepository: TableRepository,
		private translateService: TranslateService,
		private asyncOrchestrator: AsyncValidatorsOrchestratorService,
		private dataRepository: DataRepository,
		private sampleTypeProvider: SampleTypeProvider,
		private samplesTableProvider: SamplesTableProvider,
		private tableTypeProvider: TableTypeProvider,
		private dataByTableTypeProvider: DataByTableTypeProvider,
		private normalizeFormValuesForDatabaseService: NormalizeFormValuesForDatabaseService,
		private toastService: ToastService
	) {
		this.queryParamsMap = this.getQueryParamsMap();
		this.sharedData$ = this.getEntryMetadata$();
	}

	public build(isChildTable: boolean, column: ColumnModel, primaryKeys: string[]): IAsyncValidationsBuildResult {
		
		const columnName = column[WorkspaceDatabaseKeys.columns.columnName];
		
		const intervalColumns = [
			WorkspaceDatabaseKeys.columns.depthFrom,
			WorkspaceDatabaseKeys.columns.depth,
			ValidationKeys.crossFieldValidations[WorkspaceDatabaseKeys.columns.depthFrom].crossFieldName
		];

		const validationMessagesProvider = new ValidationMessagesProviderService(this.translateService);

		this.validatorFnMap = {};
		this.validationMessagesDictionary = [];
		this.validatorFnMap[columnName] = [];
		const validationBuilders = [];		

		if (columnName === WorkspaceDatabaseKeys.columns.isMinor || columnName === "depth") {
			this.addColumnValidation(columnName, this.isMinorValueChanged());
		}

		if (intervalColumns.includes(columnName)) {
			
			const crossFieldName = ValidationKeys.crossFieldValidations[columnName].crossFieldName;

			this.addColumnValidation(columnName, this.intervalValidationsChain(crossFieldName));
			this.addColumnValidationMsg(validationMessagesProvider, ValidationKeys.validationRules.intervalBreachRule);
			this.addColumnValidationMsg(validationMessagesProvider, ValidationKeys.validationRules.intervalOverlapRule);
			this.addColumnValidationMsg(validationMessagesProvider, ValidationKeys.validationRules.intervalDupRule);
			this.addColumnValidationMsg(validationMessagesProvider, ValidationKeys.validationRules.intervalGapRule);
			this.addColumnValidationMsg(
				validationMessagesProvider,
				ValidationKeys.validationRules.preventIntervalCorruptionRule
			);
			this.addColumnValidationMsg(validationMessagesProvider, ValidationKeys.validationRules.startAtZeroRule);
		}

		if (primaryKeys.includes(column.column_name)) {
			validationBuilders.push(
				new UniquePrimaryKeyBuilder(
					this.entityName,
					this.projectNumber,
					columnName,
					isChildTable,
					primaryKeys,
					this.formActionType,
					this.queryParamsProvider,
					this.dataRepository,
					this.normalizeFormValuesForDatabaseService
				)
			);
		}

		validationBuilders.push(
			new UniqueSampleNumberBuilder(
				this.entityName,
				this.childTableName,
				this.projectNumber,
				columnName,
				isChildTable,
				this.formActionType,
				primaryKeys,
				this.queryParamsProvider,
				this.dataByTableTypeProvider,
				this.standardTableProvider
			)
		);

		validationBuilders.push(
			new UniqueStandardNumberBuilder(
				this.entityName,
				this.childTableName,
				this.projectNumber,
				columnName,
				this.formActionType,
				primaryKeys,
				this.queryParamsProvider,
				this.dataByTableTypeProvider,
				this.samplesTableProvider
			)
		);
		validationBuilders.push(new NotSupportedCharactersBuilder(columnName));

		for (const vBuilder of validationBuilders) {
			const vBuilderResult: ValidationsBuilderModel | null = vBuilder.validate();

			if (vBuilderResult) {
				this.validatorFnMap[columnName].push(vBuilderResult.validation);

				for (const col of vBuilderResult.messages) {
					this.addColumnValidationMsg(validationMessagesProvider, col);
				}
			}
		}

		return {
			validations: this.validatorFnMap,
			messages: this.validationMessagesDictionary
		};
	}

	private isMinorValueChanged(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			const group = control.parent;
			const isMinorcontrol = group?.get(WorkspaceDatabaseKeys.columns.isMinor);
			const isDepthControll = group?.get(WorkspaceDatabaseKeys.columns.depth);

			const isMinorValue = isMinorcontrol?.value;

			if (!isMinorcontrol) {

				if(isDepthControll) {
					return isDepthControll?.valueChanges.pipe(
						startWith(isMinorValue),
						tap(() => {
							group
								.get(WorkspaceDatabaseKeys.columns.depth)
								.setAsyncValidators(this.intervalValidationsChain(WorkspaceDatabaseKeys.columns.depth));
							group.get(WorkspaceDatabaseKeys.columns.depth).updateValueAndValidity();
						}),
						first()
					);
				} else {
					return of(null);
				}
			}

			return isMinorcontrol?.valueChanges.pipe(
				startWith(isMinorValue),
				tap(() => {
					group
						.get(WorkspaceDatabaseKeys.columns.depthFrom)
						.setAsyncValidators(this.intervalValidationsChain(WorkspaceDatabaseKeys.columns.depthTo));
					group
						.get(WorkspaceDatabaseKeys.columns.depthTo)
						.setAsyncValidators(this.intervalValidationsChain(WorkspaceDatabaseKeys.columns.depthFrom));

					group.get(WorkspaceDatabaseKeys.columns.depthFrom).updateValueAndValidity();
					group.get(WorkspaceDatabaseKeys.columns.depthTo).updateValueAndValidity();
				}),
				first()
			);
		};
	}

	private intervalValidationsChain(crossControlKey: string): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			const group = control.parent;
			const crossControl = group?.get(crossControlKey);
			const isMinorcontrol = group?.get(WorkspaceDatabaseKeys.columns.isMinor);
			const isMinor = isMinorcontrol?.value;

			if (control?.value && crossControl?.value) {
				return this.sharedData$.pipe(
					map((sharedData: { tableMetadata: TableModel; isQcSample: boolean }) => {
						const crossTableParams: CrossTableParams = {
							tableMetadata: sharedData.tableMetadata,
							isMinor,
							controlValue: Number(control.value),
							crossControlValue: Number(crossControl.value),
							crossControlKey,
							crossControl,
							skipDupValidation:
								this.formActionType === FormActionTypeEnum.duplicate || sharedData.isQcSample,
							queryParamsMap: this.queryParamsMap
						};
						return crossTableParams;
					}),
					switchMap((crossTableParams: CrossTableParams) =>
						from(this.asyncOrchestrator.startProcess(crossTableParams))
					),
					first()
				);
			}
			return of(null);
		};
	}

	private addColumnValidation(columnName: string, validation: AsyncValidatorFn) {
		if (validation) {
			this.validatorFnMap[columnName].push(validation);
		}
	}

	private addColumnValidationMsg(
		validationMessagesProvider: ValidationMessagesProviderService,
		validationType: string,
		constraint?: IValidationConstraint,
		isColumnNumeric?: boolean
	) {
		this.validationMessagesDictionary.push(
			validationMessagesProvider.getCustomValidationMessage(validationType, constraint, isColumnNumeric)
		);
	}

	private getTableMetadata$(): Observable<TableModel> {
		// todo: use TableTypeProvider.getTableType()
		return this.tableTypeProvider
			.isFlatSamplesTable$(this.queryParamsProvider.workfileId, this.queryParamsProvider.parentTableName)
			.pipe(
				first(),
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.tableRepository
							.getTableMetadata$(this.queryParamsProvider.parentTableName)
							.pipe(shareReplay(1)),
						this.tableRepository
							.getTableMetadata$(this.queryParamsProvider.childTableName ?? this.queryParamsProvider.parentTableName)
							.pipe(shareReplay(1))
					)
				),
				catchError((error) =>
					this.toastService.presentError(`${error}: Getting table metadata failed`, ToastDuration.Slow)
				)
			);
	}

	private isQcSample$(): Observable<boolean> {
		return this.sampleTypeProvider.isQcSampleEntry$().pipe(shareReplay(1));
	}

	private getQueryParamsMap(): Map<string, string> {
		const queryParamsMap: Map<string, string> = new Map();

		queryParamsMap.set(AppRoutingKeys.queryParamsMap.tableName, this.queryParamsProvider.childTableName ?? '');
		queryParamsMap.set(AppRoutingKeys.queryParamsMap.entityName, this.queryParamsProvider.parentTableName ?? '');
		queryParamsMap.set(AppRoutingKeys.queryParamsMap.projectNumber, this.queryParamsProvider.projectNumber ?? '');
		queryParamsMap.set(AppRoutingKeys.queryParamsMap.entryId, this.queryParamsProvider.entryId ?? '');
		queryParamsMap.set(AppRoutingKeys.queryParamsMap.childEntryId, this.queryParamsProvider.childEntryId ?? '');
		queryParamsMap.set(AppRoutingKeys.queryParamsMap.workfileId, this.queryParamsProvider.workfileId ?? '');
		// console.log('cross-Table-Params', queryParamsMap);
		return queryParamsMap;
	}

	private getEntryMetadata$(): Observable<{ tableMetadata: TableModel; isQcSample: boolean }> {
		// console.log('cross-Table-Params', this.getTableMetadata$);
		return combineLatest([this.getTableMetadata$(), this.isQcSample$()]).pipe(
			map((sharedData: [tableMetadata: TableModel, isQcSample: boolean]) => ({
				tableMetadata: sharedData[0],
				isQcSample: sharedData[1]
			}))
		);
	}
}
